'use client';

import { Modal, Spinner } from '@common/components';
import { useToast } from '@common/hooks';
import SvgAdd from '@common/icon/SvgAdd';
import SvgLink from '@common/icon/SvgLink';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { AuthQueryOptions } from '@/api';
import { Image } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { BASE_URL, BREAKPOINTS } from '@/constants';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useClipboard } from '@/hooks/useClipboard';
import { useKakaoShare } from '@/hooks/useKakaoShare';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { canUseWebShare } from '@/utils/canUseWebShare';

const OG_IMAGE_URL =
  'https://cdn.welfarehello.com/static/event/home-love/market/og-image-2.png';

interface MarketShareDrawerProps {
  opened: boolean;
  onClose?: () => void;
}

export const MarketShareDrawer = ({
  opened,
  onClose,
}: MarketShareDrawerProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const ogImageUrl = OG_IMAGE_URL;

  const [webviewType, osType] = useDeviceInfoStore(
    useShallow((state) => [state.webviewType, state.osType]),
  );

  const { isWelloApp } = useCheckWelloApp();
  const { isInitialized, shareToKakao } = useKakaoShare();

  const isLogin = useAuthStore((state) => state.isLogin);

  const { data: verificationInfo } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/member-verification/info/detail'](),
    enabled: !!isLogin,
    select: (data) => data.context?.name,
  });

  const shareUrl = `${BASE_URL}/donation/event/market?utm_source=wello&utm_medium=event_button&utm_campaign=kakaoshare_1129&utm_content=event_251129`;

  const title = verificationInfo
    ? `${verificationInfo}님이 연말기념 최대 135만원\n연말 한정 선물이 도착했어요 🎁`
    : '올해 마지막 현금 선물 도착!\n최대 135만원 지금 받기';
  const description =
    '10만원 기부하면 13만원 혜택\n+ 웰로 추가 현금 선물 받아요!';

  const copy = useClipboard();

  const {
    request: checkKakaoInstallRequest,
    response: checkKakaoInstallResponse,
  } = useHandleApp({
    type: MESSAGE_KEYWORD.CHECK_KAKAO_INSTALLED,
  });

  const { request: requestShare, isPending } = useHandleApp({
    type: MESSAGE_KEYWORD.SHARE_CONTENT,
  });

  const { toast } = useToast();

  const isMobileWeb =
    !webviewType &&
    osType &&
    ['ios', 'android'].includes(osType) &&
    canUseWebShare();

  useEffect(() => {
    if (opened) {
      checkKakaoInstallRequest();
    }
  }, [checkKakaoInstallRequest, opened]);

  const handleKakaoClickInApp = async () => {
    if (isPending) return;

    if (checkKakaoInstallResponse?.isSuccess) {
      requestShare({
        share_type: 'kakao',
        url: shareUrl,
      });
      onClose?.();
    } else {
      toast({
        message: '카카오톡이 설치되어 있지 않아요',
        type: 'fail',
      });
    }
  };

  const handleKakaoClickInWeb = async () => {
    shareToKakao(
      shareUrl,
      title,
      description,
      ogImageUrl,
      '올해 마지막 선물받기',
    );
    onClose?.();
  };

  const handleKakaoClick = () => {
    if (isWelloApp) {
      handleKakaoClickInApp();

      return;
    }

    handleKakaoClickInWeb();
  };

  const content = (
    <div
      className={cn('mx-42 flex justify-around gap-6', {
        'justify-between': isWelloApp,
      })}
    >
      {webviewType !== WEBVIEW_TYPE.KB_PAY ? (
        <KakaoButton isLoading={!isInitialized} onClick={handleKakaoClick} />
      ) : null}
      <button
        type="button"
        className="flex cursor-pointer flex-col items-center justify-center gap-6 border-none"
        onClick={() => {
          try {
            copy({ value: shareUrl });
          } finally {
            onClose?.();
          }
        }}
      >
        <div className="flex size-48 items-center justify-center rounded-pill bg-Gray-500 text-Gray-white">
          <SvgLink />
        </div>
        <h4 className="font-400 text-Gray-900 typo-body4">URL 복사</h4>
      </button>
      {isWelloApp || isMobileWeb ? (
        <button
          type="button"
          className="flex cursor-pointer flex-col items-center justify-center gap-6 border-none"
          onClick={() => {
            try {
              if (isWelloApp) {
                return requestShare({
                  share_type: 'uni',
                  url: shareUrl,
                });
              }

              if (isMobileWeb) {
                return navigator.share({
                  url: shareUrl,
                });
              }
            } finally {
              onClose?.();
            }
          }}
        >
          <div className="flex size-48 items-center justify-center rounded-pill bg-Gray-200 text-Gray-black">
            <SvgAdd />
          </div>
          <h4 className="font-400 text-Gray-900 typo-body4">더 보기</h4>
        </button>
      ) : null}
    </div>
  );

  if (isDesktopViewport) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        asChild
        className="w-full max-w-[480px]"
        closeButton
      >
        <div>{content}</div>
      </Modal>
    );
  }

  return (
    <ResponsiveDialog
      opened={opened}
      onClose={onClose}
      ariaLabel="공유하기"
      drawerType="bottom"
      drawerNoPadding
    >
      <div className="pb-16">{content}</div>
    </ResponsiveDialog>
  );
};

interface KakaoButtonProps {
  isLoading: boolean;
  onClick: () => void;
}

const KakaoButton = ({ isLoading, onClick }: KakaoButtonProps) => {
  return (
    <button
      type="button"
      disabled={isLoading}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-6 border-none',
        { 'cursor-not-allowed opacity-50': isLoading },
      )}
      onClick={onClick}
    >
      <div className="flex size-48 items-center justify-center rounded-pill bg-[#fbdf03]">
        {isLoading ? (
          <Spinner size={24} />
        ) : (
          <Image
            alt="kakao"
            height={24}
            src="/assets/pngs/login/kakao.png"
            width={24}
          />
        )}
      </div>
      <span className="font-400 text-Gray-900 typo-body4">카카오톡</span>
    </button>
  );
};
